import PropTypes from "prop-types";
import { omit } from "lodash";
import calculatePayment, { affordability, monthlyPMI, monthlyPI } from "@zillow/mortgage-calculations";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG, getIsNanError, castAsNumberIfNotNan } from "../../utils/zhl-calculator.esm.js";
const {
  default: calculateAffordability,
  affordabilityDefaults
} = affordability;
const AFFORDABILITY_DTI_MAX = 50;
function isAggressive({
  dti
}) {
  return dti > 36;
}
const FieldConfig = {
  customPayment: {
    name: "customPayment"
  },
  maximumPayment: {
    name: "maximumPayment"
    // No config is needed, as this field is programatically controlled. Also
    // if it does get invalid, the user cannot address it, as there is no
    // corresponding field for it, visually. The reason it is still a field
    // though is because a default value can be set.
  },
  annualIncome: {
    name: "annualIncome",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  monthlyDebts: {
    name: "monthlyDebts",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  downPayment: {
    name: "downPayment",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  dti: {
    name: "dti",
    config: {
      max: {
        value: AFFORDABILITY_DTI_MAX,
        message: `Must be ${AFFORDABILITY_DTI_MAX} at most.`
      },
      ...COMMON_PERCENT_CONFIG
    }
  },
  rate: {
    name: "rate",
    config: {
      ...omit(COMMON_PERCENT_CONFIG, "min"),
      validate: {
        ...COMMON_PERCENT_CONFIG.validate,
        getIsLessThanOrEqualToZeroError: (value) => {
          let error = null;
          const castedNumber = Number(value);
          if (castedNumber <= 0) {
            error = "Must be greater than 0%.";
          }
          return error;
        }
      }
    }
  },
  term: {
    name: "term",
    config: {
      setValueAs: castAsNumberIfNotNan,
      required: "Required",
      min: {
        value: 1,
        message: "Must be at least 1."
      },
      max: {
        value: 500,
        message: "Must be 500 at most."
      },
      validate: {
        nan: getIsNanError("Invalid number")
      }
    }
  },
  includePMI: {
    name: "includePMI"
  },
  includeTaxesInsurance: {
    name: "includeTaxesInsurance"
  },
  propertyTaxRate: {
    name: "propertyTaxRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  annualHomeownersInsurance: {
    name: "annualHomeownersInsurance",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  monthlyHOA: {
    name: "monthlyHOA",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  monthlyPayment: {
    name: "monthlyPayment",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  }
};
const DEFAULT_VALUES_SHAPE = {
  customPayment: PropTypes.bool,
  maximumPayment: PropTypes.number,
  annualIncome: PropTypes.number,
  monthlyDebts: PropTypes.number,
  downPayment: PropTypes.number,
  dti: PropTypes.number,
  rate: PropTypes.number,
  term: PropTypes.number,
  includePMI: PropTypes.bool,
  includeTaxesInsurance: PropTypes.bool,
  propertyTaxRate: PropTypes.number,
  annualHomeownersInsurance: PropTypes.number,
  monthlyHOA: PropTypes.number,
  monthlyPayment: PropTypes.number
};
function getCalculatedAffordabiltyValues(currentFields) {
  const affordabilityValues = affordabilityDefaults({
    ...currentFields,
    includeHomeownersInsurance: currentFields.includeTaxesInsurance,
    includeHOA: true,
    includePropertyTax: currentFields.includeTaxesInsurance
  });
  const affordabilityOutputs = calculateAffordability(affordabilityValues);
  return {
    ...affordabilityOutputs,
    monthlyPayment: calculatePayment({
      homePrice: affordabilityOutputs.affordabilityByDTI,
      ...affordabilityValues
    }).monthlyPayment
  };
}
function useLastValidAffordabilityValues(form) {
  return useLastValidValues(form, getAffordabilityValues);
}
function getAffordabilityValues(lastValidFields) {
  const affordability2 = getCalculatedAffordabiltyValues(lastValidFields);
  const calculations = {
    affordability: affordability2,
    pi: monthlyPI(affordability2.affordabilityByDTI - lastValidFields.downPayment, lastValidFields.rate, lastValidFields.term),
    pmi: monthlyPMI(affordability2.affordabilityByDTI - lastValidFields.downPayment, affordability2.affordabilityByDTI, false),
    aggressive: isAggressive(lastValidFields)
  };
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
export {
  AFFORDABILITY_DTI_MAX,
  DEFAULT_VALUES_SHAPE,
  FieldConfig,
  getCalculatedAffordabiltyValues,
  isAggressive,
  useLastValidAffordabilityValues
};
